import { useEffect, useState, useCallback } from 'react';

/**
 * Best-effort client-side protections against print/screenshots. No web app can
 * fully prevent screenshots at the OS level, but layered defenses deter casual
 * capture and blur the frame when detection triggers.
 *
 * Returns `{ attackDetected }` so callers (e.g. lightbox) can render an alert.
 *
 * Detections (layered):
 *  - PrintScreen key (Windows)
 *  - Cmd+Shift+3/4/5/6 (macOS screenshot shortcuts)
 *  - Win+Shift+S (Windows Snip)
 *  - Ctrl/Meta+P (print), Ctrl/Meta+S (save)
 *  - Ctrl/Meta+C on image (copy)
 *  - Right-click, drag, text selection
 *  - Window blur / tab visibilitychange (screenshot tools steal focus)
 *  - `beforeprint` / `print` media query
 */
export function useAntiScreenshot(enabled = true) {
  const [attackDetected, setAttackDetected] = useState(false);

  const flashBlur = useCallback((reason = 'attempt') => {
    const root = document.documentElement;
    root.classList.add('protected-blur');
    setAttackDetected(true);
    // Clipboard write may reject with a Promise — swallow silently.
    try {
      const p = navigator.clipboard?.writeText?.('');
      if (p && typeof p.catch === 'function') p.catch(() => {});
    } catch (_) {}
    // Keep blurred for a few seconds — long enough to defeat screenshots.
    clearTimeout(flashBlur._t);
    flashBlur._t = setTimeout(() => {
      root.classList.remove('protected-blur');
      setAttackDetected(false);
    }, 3000);
  }, []);

  useEffect(() => {
    if (!enabled) return;

    const prevent = (e) => { e.preventDefault(); return false; };

    document.addEventListener('contextmenu', prevent);
    document.addEventListener('dragstart', prevent);
    document.addEventListener('selectstart', prevent);
    document.addEventListener('copy', (e) => { e.preventDefault(); flashBlur('copy'); });

    const onKey = (e) => {
      const k = e.key;
      // PrintScreen on Windows/Linux
      if (k === 'PrintScreen' || e.keyCode === 44) {
        flashBlur('printscreen');
        e.preventDefault();
        return false;
      }
      // macOS screenshot shortcuts: Cmd+Shift+3/4/5/6
      if (e.metaKey && e.shiftKey && ['3', '4', '5', '6'].includes(k)) {
        flashBlur('mac-screenshot');
        e.preventDefault();
        return false;
      }
      // Windows Snip: Win+Shift+S
      if (e.shiftKey && (k === 'S' || k === 's') && (e.metaKey || e.getModifierState?.('OS'))) {
        flashBlur('win-snip');
        e.preventDefault();
        return false;
      }
      // Print / Save / Copy shortcuts
      if ((e.ctrlKey || e.metaKey) && ['p', 'P', 's', 'S', 'c', 'C'].includes(k)) {
        flashBlur(k.toLowerCase());
        e.preventDefault();
        return false;
      }
      // Devtools
      if (k === 'F12') {
        e.preventDefault();
        return false;
      }
    };
    document.addEventListener('keydown', onKey, true);
    document.addEventListener('keyup', onKey, true);

    const onBlur = () => flashBlur('blur');
    const onFocus = () => {
      // Focus returned — allow normal viewing again quickly.
      document.documentElement.classList.remove('protected-blur');
    };
    window.addEventListener('blur', onBlur);
    window.addEventListener('focus', onFocus);

    const onVis = () => {
      if (document.hidden) flashBlur('hidden');
    };
    document.addEventListener('visibilitychange', onVis);

    const mql = window.matchMedia('print');
    const onPrint = () => flashBlur('media-print');
    if (mql.addEventListener) mql.addEventListener('change', onPrint);
    window.addEventListener('beforeprint', onPrint);
    window.addEventListener('afterprint', () => document.documentElement.classList.remove('protected-blur'));

    return () => {
      document.removeEventListener('contextmenu', prevent);
      document.removeEventListener('dragstart', prevent);
      document.removeEventListener('selectstart', prevent);
      document.removeEventListener('keydown', onKey, true);
      document.removeEventListener('keyup', onKey, true);
      window.removeEventListener('blur', onBlur);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('beforeprint', onPrint);
      if (mql.removeEventListener) mql.removeEventListener('change', onPrint);
    };
  }, [enabled, flashBlur]);

  return { attackDetected };
}
